import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Editorial button. Pill-shaped by default, ink on cream, with an optional
 * outline variant. Sizes mirror the design's 16/14/12 vertical rhythm.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-pill font-sans font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-foreground text-background hover:bg-foreground/90 active:bg-foreground/85",
        outline:
          "border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background",
        accent:
          "bg-accent text-accent-foreground hover:bg-accent/90",
        ghost:
          "bg-transparent text-foreground hover:bg-bone",
        cream:
          "bg-background text-foreground hover:bg-background/85",
        link:
          "text-foreground underline-offset-4 hover:underline rounded-none px-0",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      },
      size: {
        sm: "h-9 px-4 text-[12px]",
        default: "h-11 px-5 text-[13px]",
        lg: "h-13 px-7 text-[14px] py-3.5",
        xl: "h-16 px-8 text-[15px] py-5",
        icon: "h-11 w-11",
        "icon-lg": "h-14 w-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
